import React, { useState, useEffect } from "react";
import { Dropdown } from "react-bootstrap";
import { FaDownload, FaFileCsv, FaFilePdf, FaFileExcel, FaArchive, FaTrash } from "react-icons/fa";
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import prescriptionService from '../services/prescriptionService';

const ActionButton = ({ selectedPrescriptions = [], onBulkDelete, refreshData, apiBaseUrl = '/api' }) => {
  const [isDeleting, setIsDeleting] = useState(false);
  const [isArchiving, setIsArchiving] = useState(false);
  const [allSameStatus, setAllSameStatus] = useState(true);
  const [currentStatus, setCurrentStatus] = useState(null);

  // Check if all selected prescriptions have the same status (active or inactive)
  useEffect(() => {
    if (selectedPrescriptions.length > 0) {
      const firstStatus = selectedPrescriptions[0].isActive !== undefined ? 
        selectedPrescriptions[0].isActive : true;
      
      const allSame = selectedPrescriptions.every(prescription => {
        const status = prescription.isActive !== undefined ? prescription.isActive : true;
        return status === firstStatus;
      });
      
      setAllSameStatus(allSame);
      setCurrentStatus(firstStatus);
    } else {
      setAllSameStatus(true);
      setCurrentStatus(null);
    }
  }, [selectedPrescriptions]);

  const exportToPDF = (prescriptions) => {
    if (prescriptions.length === 0) return;
  
    const doc = new jsPDF({
      orientation: 'landscape',
      unit: 'mm',
      format: 'a4'
    });
    
    doc.setFontSize(16);
    doc.text("Prescription Details", doc.internal.pageSize.getWidth() / 2, 15, { align: 'center' });
  
    const allKeys = new Set();
    prescriptions.forEach(prescription => {
      Object.keys(prescription).forEach(key => {
        if (!key.startsWith('_') && key !== '__v') {
          allKeys.add(key);
        }
      });
    });
  
    const preferredOrder = [
      'id', '_id', 'patientId', 'patientName', 'doctorId', 'doctorName', 
      'medicineId', 'medicineName', 'dosage', 'frequency', 'duration', 
      'refills', 'instructions', 'datePrescribed', 'symptoms', 'isActive'
    ];
    
    const sortedKeys = Array.from(allKeys).sort((a, b) => {
      const indexA = preferredOrder.indexOf(a);
      const indexB = preferredOrder.indexOf(b);
      
      if (indexA !== -1 && indexB !== -1) return indexA - indexB;
      if (indexA !== -1) return -1;
      if (indexB !== -1) return 1;
      return a.localeCompare(b);
    });
  
    const formattedPrescriptions = prescriptions.map(prescription => {
      const formatted = {};
      
      sortedKeys.forEach(key => {
        if (!prescription.hasOwnProperty(key)) {
          formatted[key] = '';
          return;
        }
        
        let value = prescription[key];
        
        if (typeof value === 'string' && value.match(/^\d{4}-\d{2}-\d{2}/)) {
          try {
            const date = new Date(value);
            if (!isNaN(date.getTime())) {
              value = date.toLocaleDateString();
            }
          } catch (e) {}
        }
        
        if (typeof value === 'boolean') {
          value = value ? 'Yes' : 'No';
        }
        
        if (value !== null && typeof value === 'object') {
          try {
            value = JSON.stringify(value)
              .replace(/[{}"[\]]/g, '')
              .replace(/,/g, ', ')
              .trim();
          } catch (e) {
            value = String(value);
          }
        }
        
        value = value !== null && value !== undefined ? String(value) : '';
        formatted[key] = value;
      });
      
      return formatted;
    });
  
    const formattedHeaders = sortedKeys.map(key => {
      return key
        .replace(/([A-Z])/g, ' $1')
        .replace(/^./, str => str.toUpperCase())
        .trim();
    });
  
    const tableColumns = formattedHeaders;
    const tableRows = formattedPrescriptions.map(prescription => 
      sortedKeys.map(key => prescription[key] || '')
    );
  
    const columnWidths = {};
    sortedKeys.forEach((key, index) => {
      let columnWidth;
      
      if (key === 'id' || key === '_id' || key.endsWith('Id')) {
        columnWidth = 25;
      } else if (key.includes('name') || key.includes('Name')) {
        columnWidth = 30;
      } else if (key === 'instructions' || key === 'symptoms') {
        columnWidth = 45;
      } else if (key === 'isActive') {
        columnWidth = 15;
      } else if (key === 'dosage' || key === 'refills') {
        columnWidth = 15;
      } else if (key === 'duration' || key === 'frequency') {
        columnWidth = 20;
      } else {
        columnWidth = 25;
      }
      
      columnWidths[index] = { cellWidth: columnWidth };
    });
  
    const pageWidth = doc.internal.pageSize.getWidth() - 20;
    const totalWidthUnits = Object.values(columnWidths).reduce((sum, col) => sum + col.cellWidth, 0);
    const scaleRatio = pageWidth / totalWidthUnits;
    
    Object.keys(columnWidths).forEach(index => {
      columnWidths[index].cellWidth = columnWidths[index].cellWidth * scaleRatio;
    });
  
    autoTable(doc, {
      head: [tableColumns],
      body: tableRows,
      startY: 20,
      styles: {
        fontSize: 8,
        cellPadding: 2,
        overflow: 'linebreak',
        lineColor: [200, 200, 200],
        lineWidth: 0.1,
        halign: 'left',
        valign: 'middle',
      },
      headStyles: {
        fillColor: [220, 220, 220],
        textColor: [0, 0, 0],
        fontStyle: 'bold',
        halign: 'center',
      },
      columnStyles: columnWidths,
      margin: { top: 20, right: 10, bottom: 15, left: 10 },
      tableWidth: 'auto',
      showHead: 'everyPage',
      didDrawPage: (data) => {
        const pageCount = doc.internal.getNumberOfPages();
        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();
        
        doc.setFontSize(8);
        doc.setTextColor(100);
        doc.text(
          `Page ${data.pageNumber} of ${pageCount}`,
          pageWidth / 2,
          pageHeight - 5,
          { align: 'center' }
        );
        
        const dateText = `Generated: ${new Date().toLocaleString()} | Total Records: ${tableRows.length}`;
        doc.text(dateText, 10, pageHeight - 5);
        
        if (data.pageNumber > 1) {
          doc.setFontSize(14);
          doc.setTextColor(0);
          doc.text("Prescription Details (Continued)", pageWidth / 2, 10, { align: 'center' });
        }
      }
    });
  
    const date = new Date().toISOString().slice(0, 10);
    doc.save(`prescriptions_${date}.pdf`);
  };
  
  const exportToCSV = (prescriptions) => {
    if (prescriptions.length === 0) return;
  
    const preferredOrder = [
      'id', '_id', 'patientId', 'patientName', 'doctorId', 'doctorName', 
      'medicineId', 'medicineName', 'dosage', 'frequency', 'duration', 
      'refills', 'instructions', 'datePrescribed', 'symptoms', 'isActive'
    ];
  
    const allKeys = new Set();
    prescriptions.forEach(prescription => {
      Object.keys(prescription).forEach(key => {
        if (!key.startsWith('_') && key !== '__v') {
          allKeys.add(key);
        }
      });
    });
  
    const sortedKeys = Array.from(allKeys).sort((a, b) => {
      const indexA = preferredOrder.indexOf(a);
      const indexB = preferredOrder.indexOf(b);
      
      if (indexA !== -1 && indexB !== -1) return indexA - indexB;
      if (indexA !== -1) return -1;
      if (indexB !== -1) return 1;
      return a.localeCompare(b);
    });
  
    const processedPrescriptions = prescriptions.map(prescription => {
      const processed = {};
      
      sortedKeys.forEach(key => {
        if (!prescription.hasOwnProperty(key)) {
          processed[key] = '';
          return;
        }
        
        let value = prescription[key];
        
        if (value && typeof value === 'string' && 
            (value.match(/^\d{4}-\d{2}-\d{2}T/) || value.match(/^\d{4}-\d{2}-\d{2}/))) {
          try {
            const date = new Date(value);
            if (!isNaN(date.getTime())) {
              processed[key] = date.toLocaleDateString();
            } else {
              processed[key] = value;
            }
          } catch (e) {
            processed[key] = value;
          }
        } 
        else if (typeof value === 'object' && value !== null) {
          try {
            processed[key] = JSON.stringify(value)
              .replace(/[{}"[\]]/g, '')
              .replace(/,/g, ', ')
              .trim();
          } catch (e) {
            processed[key] = String(value);
          }
        }
        else if (typeof value === 'boolean') {
          processed[key] = value ? 'Yes' : 'No';
        }
        else if (value === undefined || value === null) {
          processed[key] = '';
        }
        else {
          processed[key] = value;
        }
      });
      
      return processed;
    });
  
    const headers = sortedKeys.map(key => {
      return key
        .replace(/([A-Z])/g, ' $1')
        .replace(/^./, str => str.toUpperCase())
        .trim();
    });
    
    const headerRow = headers
      .map(header => `"${header}"`)
      .join(',');
    
    const dataRows = processedPrescriptions.map(prescription => 
      sortedKeys.map(key => {
        const value = prescription[key];
        if (value === null || value === undefined || value === '') return '""';
        return `"${String(value).replace(/"/g, '""')}"`;
      }).join(',')
    ).join('\n');
    
    const csvContent = `data:text/csv;charset=utf-8,${headerRow}\n${dataRows}`;
    
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    
    const date = new Date().toISOString().slice(0, 10);
    link.setAttribute("download", `prescriptions_${date}.csv`);
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  
  const exportToExcel = (prescriptions) => {
    if (prescriptions.length === 0) return;
  
    const processedPrescriptions = prescriptions.map(prescription => {
      const processed = {};
      
      Object.keys(prescription).forEach(key => {
        if (prescription[key] && typeof prescription[key] === 'string' && 
            prescription[key].match(/^\d{4}-\d{2}-\d{2}T/)) {
          processed[key] = prescription[key];
        } 
        else if (typeof prescription[key] === 'object' && prescription[key] !== null) {
          processed[key] = JSON.stringify(prescription[key]);
        }
        else {
          processed[key] = prescription[key];
        }
      });
      
      return processed;
    });
  
    const worksheet = XLSX.utils.json_to_sheet(processedPrescriptions);
    
    const range = XLSX.utils.decode_range(worksheet['!ref']);
    
    const columnWidths = [];
    for (let C = range.s.c; C <= range.e.c; ++C) {
      let maxColumnWidth = 10;
      
      for (let R = range.s.r; R <= range.e.r; ++R) {
        const cellAddress = XLSX.utils.encode_cell({r: R, c: C});
        const cell = worksheet[cellAddress];
        
        if (!cell) continue;
        
        const cellContent = String(cell.v || '');
        const estimatedWidth = Math.min(50, cellContent.length * 1.2);
        maxColumnWidth = Math.max(maxColumnWidth, estimatedWidth);
      }
      
      columnWidths[C] = { width: maxColumnWidth };
    }
    
    worksheet['!cols'] = columnWidths;
    
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Prescriptions");
    
    for (let C = range.s.c; C <= range.e.c; ++C) {
      const headerAddress = XLSX.utils.encode_cell({r: 0, c: C});
      if (!worksheet[headerAddress]) continue;
      
      const headerText = worksheet[headerAddress].v;
      worksheet[headerAddress].v = typeof headerText === 'string' ? 
        headerText.charAt(0).toUpperCase() + headerText.slice(1) : 
        headerText;
    }
    
    const date = new Date().toISOString().slice(0, 10);
    XLSX.writeFile(workbook, `prescriptions_${date}.xlsx`);
  };
  
  const printPrescriptions = (prescriptions) => {
    if (prescriptions.length === 0) return;
  
    const preferredOrder = [
      'id', '_id', 'patientId', 'patientName', 'doctorId', 'doctorName', 
      'medicineId', 'medicineName', 'dosage', 'frequency', 'duration', 
      'refills', 'instructions', 'datePrescribed', 'symptoms', 'isActive'
    ];
  
    const allKeys = new Set();
    prescriptions.forEach(prescription => {
      Object.keys(prescription).forEach(key => {
        if (!key.startsWith('_') && key !== '__v') {
          allKeys.add(key);
        }
      });
    });
  
    const sortedKeys = Array.from(allKeys).sort((a, b) => {
      const indexA = preferredOrder.indexOf(a);
      const indexB = preferredOrder.indexOf(b);
      
      if (indexA !== -1 && indexB !== -1) return indexA - indexB;
      if (indexA !== -1) return -1;
      if (indexB !== -1) return 1;
      return a.localeCompare(b);
    });
  
    const processedData = prescriptions.map(prescription => {
      const processed = {};
      
      sortedKeys.forEach(key => {
        if (!prescription.hasOwnProperty(key)) {
          processed[key] = '';
          return;
        }
        
        let value = prescription[key];
        
        if (value && typeof value === 'string' && 
            (value.match(/^\d{4}-\d{2}-\d{2}T/) || value.match(/^\d{4}-\d{2}-\d{2}/))) {
          try {
            const date = new Date(value);
            if (!isNaN(date.getTime())) {
              processed[key] = date.toLocaleDateString();
            } else {
              processed[key] = value;
            }
          } catch (e) {
            processed[key] = value;
          }
        } 
        else if (typeof value === 'object' && value !== null) {
          try {
            processed[key] = JSON.stringify(value)
              .replace(/[{}"[\]]/g, '')
              .replace(/,/g, ', ')
              .trim();
          } catch (e) {
            processed[key] = String(value);
          }
        }
        else if (typeof value === 'boolean') {
          processed[key] = value ? 'Yes' : 'No';
        }
        else if (value === undefined || value === null) {
          processed[key] = '';
        }
        else {
          processed[key] = value;
        }
      });
      
      return processed;
    });
      
    const headers = sortedKeys.map(key => {
      return key
        .replace(/([A-Z])/g, ' $1')
        .replace(/^./, str => str.toUpperCase())
        .trim();
    });
    
    const printWindow = window.open('', 'PRINT', 'height=800,width=1200');
    
    const tableHTML = `
      <html>
        <head>
          <title>Prescription Details</title>
          <style>
            body { 
              font-family: Arial, sans-serif; 
              margin: 0;
              padding: 20px;
            }
            h1 { 
              text-align: center; 
              margin-bottom: 20px;
            }
            .print-info {
              display: flex;
              justify-content: space-between;
              margin-bottom: 20px;
              font-size: 12px;
            }
            table { 
              width: 100%; 
              border-collapse: collapse; 
              margin-bottom: 20px;
              font-size: 11px;
            }
            th, td { 
              border: 1px solid #ddd; 
              padding: 6px; 
              text-align: left;
              vertical-align: top;
            }
            th { 
              background-color: #f2f2f2; 
              font-weight: bold;
              position: sticky;
              top: 0;
            }
            tr:nth-child(even) {
              background-color: #f9f9f9;
            }
            @page { 
              size: landscape; 
              margin: 0.5in;
            }
            thead { display: table-header-group; }
            tr { page-break-inside: avoid; }
            td.long-text {
              max-width: 200px;
              word-wrap: break-word;
            }
            ${sortedKeys.map((key, index) => {
              let width;
              if (key === 'id' || key === '_id' || key.endsWith('Id')) {
                width = '80px';
              } else if (key.includes('name') || key.includes('Name')) {
                width = '120px';
              } else if (key === 'instructions' || key === 'symptoms') {
                width = '200px; overflow-wrap: break-word;';
              } else if (key === 'isActive') {
                width = '60px';
              } else if (key === 'dosage' || key === 'refills') {
                width = '60px';
              } else if (key === 'duration' || key === 'frequency') {
                width = '80px';
              } else if (key.includes('date') || key.includes('Date')) {
                width = '100px';
              } else {
                width = '100px';
              }
              return `.col-${index} { width: ${width}; }`;
            }).join('\n')}
          </style>
        </head>
        <body>
          <h1>Prescription Details</h1>
          <div class="print-info">
            <div>Total Items: ${processedData.length}</div>
            <div>Generated on: ${new Date().toLocaleString()}</div>
          </div>
          <table>
            <thead>
              <tr>
                ${headers.map((header, index) => `<th class="col-${index}">${header}</th>`).join('')}
              </tr>
            </thead>
            <tbody>
              ${processedData.map(row => 
                `<tr>
                  ${sortedKeys.map((key, index) => {
                    const value = row[key] !== null && row[key] !== undefined ? row[key] : '';
                    const className = (key === 'instructions' || key === 'symptoms') ? 'long-text' : '';
                    return `<td class="col-${index} ${className}">${value}</td>`;
                  }).join('')}
                </tr>`
              ).join('')}
            </tbody>
          </table>
          <script>
            window.onload = function() {
              setTimeout(function() {
                window.print();
              }, 500);
            };
          </script>
        </body>
      </html>
    `;
  
    printWindow.document.write(tableHTML);
    printWindow.document.close();
    printWindow.focus();
  };

  const archivePrescriptions = async (prescriptions) => {
    if (!allSameStatus) {
      alert('Cannot archive/unarchive prescriptions with different status states. Please select only active or only inactive prescriptions.');
      return;
    }

    const newStatus = !currentStatus;
    const actionName = currentStatus ? 'archive' : 'unarchive';
    
    if (window.confirm(`Are you sure you want to ${actionName} ${prescriptions.length} prescriptions?`)) {
      setIsArchiving(true);
      
      let successfulOperations = 0;
      const operationErrors = [];
      
      const updatePromises = prescriptions.map(prescription => {
        const prescriptionId = prescription._id || prescription.id;
        console.log(`${actionName}ing prescription with ID:`, prescriptionId);
        
        return prescriptionService.updatePrescriptionActiveStatus(prescriptionId, newStatus)
          .then(response => {
            successfulOperations++;
            console.log(`Successfully ${actionName}d prescription ${prescriptionId}`);
            return { success: true, id: prescriptionId };
          })
          .catch(error => {
            console.error(`Error ${actionName}ing prescription ${prescriptionId}:`, error);
            operationErrors.push(`ID ${prescriptionId}: ${error.message || 'Unknown error'}`);
            return { success: false, id: prescriptionId, error };
          });
      });
      
      try {
        const results = await Promise.all(updatePromises);
        
        if (successfulOperations === prescriptions.length) {
          alert(`Successfully ${actionName}d all ${prescriptions.length} prescriptions`);
        } else {
          const errorMessage = `${actionName.charAt(0).toUpperCase() + actionName.slice(1)}d ${successfulOperations} out of ${prescriptions.length} prescriptions. Errors: ${operationErrors.join('; ')}`;
          console.warn(errorMessage);
          alert(errorMessage);
        }
        
        console.log('Refreshing data and KPIs after bulk status update');
        if (refreshData) {
          refreshData();
        } else {
          window.location.reload();
        }
      } catch (error) {
        console.error(`Error in bulk ${actionName}:`, error);
        alert(`An unexpected error occurred: ${error.message}`);
        
        if (refreshData) {
          refreshData();
        }
      } finally {
        setIsArchiving(false);
      }
    }
  };

  const deletePrescriptions = async (prescriptions) => {
    if (prescriptions.length === 0) return;
    
    const idList = prescriptions.map(prescription => {
      return prescription._id || prescription.id;
    }).join(", ");
    
    if (window.confirm(`Are you sure you want to delete the prescriptions with IDs: ${idList}?`)) {
      setIsDeleting(true);
      
      try {
        let successfulDeletions = 0;
        const deletionErrors = [];
        
        for (const prescription of prescriptions) {
          try {
            const prescriptionId = prescription._id || prescription.id;
            console.log('Deleting prescription with ID:', prescriptionId);
            
            await prescriptionService.deletePrescription(prescriptionId);
            successfulDeletions++;
            console.log(`Successfully deleted prescription ${prescriptionId}`);
          } catch (error) {
            const prescriptionId = prescription._id || prescription.id;
            console.error(`Error deleting prescription ${prescriptionId}:`, error);
            deletionErrors.push(`ID ${prescriptionId}: ${error.message || 'Unknown error'}`);
          }
        }
        
        if (successfulDeletions === prescriptions.length) {
          alert(`Successfully deleted all ${prescriptions.length} prescriptions`);
        } else {
          const errorMessage = `Deleted ${successfulDeletions} out of ${prescriptions.length} prescriptions. Errors: ${deletionErrors.join('; ')}`;
          console.warn(errorMessage);
          alert(errorMessage);
        }
        
        if (onBulkDelete && successfulDeletions > 0) {
          onBulkDelete(prescriptions.filter((p, index) => !deletionErrors.some(e => e.includes(p._id || p.id))));
        }
      } catch (error) {
        console.error('Error in bulk deletion:', error);
        alert(`An unexpected error occurred during deletion: ${error.message}`);
      } finally {
        setIsDeleting(false);
        
        console.log('Refreshing data after deletion');
        if (refreshData) {
          refreshData();
        } else {
          window.location.reload();
        }
      }
    }
  };

  const getArchiveButtonLabel = () => {
    if (!allSameStatus) {
      return "Mixed Status Selected";
    }
    return currentStatus ? "Archive Selected" : "Unarchive Selected";
  };

  const handleActionClick = (action) => {
    if (selectedPrescriptions.length > 0) {
      console.log(`${action} clicked for ${selectedPrescriptions.length} prescriptions`);
      
      switch(action) {
        case "Print":
          printPrescriptions(selectedPrescriptions);
          break;
        case "Export CSV":
          exportToCSV(selectedPrescriptions);
          break;
        case "Export PDF":
          exportToPDF(selectedPrescriptions);
          break;
        case "Export Excel":
          exportToExcel(selectedPrescriptions);
          break;
        case "Archive":
          archivePrescriptions(selectedPrescriptions);
          break;
        case "Delete":
          deletePrescriptions(selectedPrescriptions);
          break;
        default:
          console.warn(`Unknown action: ${action}`);
          break;
      }
    }
  };

  return (
    <div className="flex-grow-sm-0">
      <Dropdown>
        <Dropdown.Toggle
          id="action-dropdown"
          className={`btn btn-gray text-start w-100 d-flex align-items-center justify-content-between px-4 py-2 border-0 rounded-3 shadow-sm dropdownbtn`}
          disabled={isDeleting || isArchiving}
        >
          <span>
            {isDeleting ? 'Deleting...' : 
             isArchiving ? 'Processing...' : 
             `Action${selectedPrescriptions.length > 0 ? ` (${selectedPrescriptions.length})` : ''}`}
          </span>
        </Dropdown.Toggle>

        <Dropdown.Menu className="dropdown-menu w-100 bg-white fs-sm fw-semibold border-0 rounded-3 shadow-sm mt-2">
          <Dropdown.Item
            onClick={() => handleActionClick("Print")}
            className="dropdown-item d-flex align-items-center gap-2 py-2"
            disabled={selectedPrescriptions.length === 0 || isDeleting || isArchiving}
          >
            <FaDownload className="icon-sm" />
            <span>Print Prescriptions</span>
          </Dropdown.Item>

          <Dropdown.Divider />

          <Dropdown.Item
            onClick={() => handleActionClick("Export CSV")}
            className="dropdown-item d-flex align-items-center gap-2 py-2"
            disabled={selectedPrescriptions.length === 0 || isDeleting || isArchiving}
          >
            <FaFileCsv className="icon-sm" />
            <span>Download CSV</span>
          </Dropdown.Item>
          <Dropdown.Item
            onClick={() => handleActionClick("Export PDF")}
            className="dropdown-item d-flex align-items-center gap-2 py-2"
            disabled={selectedPrescriptions.length === 0 || isDeleting || isArchiving}
          >
            <FaFilePdf className="icon-sm" />
            <span>Download PDF</span>
          </Dropdown.Item>
          <Dropdown.Item
            onClick={() => handleActionClick("Export Excel")}
            className="dropdown-item d-flex align-items-center gap-2 py-2"
            disabled={selectedPrescriptions.length === 0 || isDeleting || isArchiving}
          >
            <FaFileExcel className="icon-sm" />
            <span>Download Excel</span>
          </Dropdown.Item>

          <Dropdown.Divider />

          <Dropdown.Item
            onClick={() => handleActionClick("Archive")}
            className="dropdown-item d-flex align-items-center gap-2 py-2"
            disabled={selectedPrescriptions.length === 0 || isDeleting || isArchiving || !allSameStatus}
            title={!allSameStatus ? "Cannot archive/unarchive prescriptions with mixed status" : ""}
          >
            <FaArchive className="icon-sm" />
            <span>{isArchiving ? 'Processing...' : getArchiveButtonLabel()}</span>
          </Dropdown.Item>

          <Dropdown.Item
            onClick={() => handleActionClick("Delete")}
            className="dropdown-item d-flex align-items-center gap-2 py-2"
            disabled={selectedPrescriptions.length === 0 || isDeleting || isArchiving}
          >
            <FaTrash className="icon-sm" />
            <span>{isDeleting ? 'Deleting...' : 'Delete Selected'}</span>
          </Dropdown.Item>
        </Dropdown.Menu>
      </Dropdown>
    </div>
  );
};

export default ActionButton;